/**
 * OrderActionsToolbar — consolidated order-detail commands (#1204, #1804).
 *
 * The Re-fetch affordance is mounted through
 * `orderResource.detail.headerActions`, so it sits in the detail header
 * beside Edit. The header is on screen whichever tab the rail shows, which is
 * why commands live there and not on a tab (#1804 — same shape as dispatch's
 * `JobActionsToolbar`).
 *
 * Self-gating (mirrors dispatch `JobActionsToolbar`): each action carries its
 * own permission gate, and the whole toolbar renders NOTHING when the operator
 * has no grant — the repo forbids the hidden-disabled-button anti-pattern, so
 * the gate returns `null` rather than an empty shell.
 *
 * The toolbar stays a toolbar with one button in it: the shape is what makes
 * the NEXT order command a one-line addition rather than a re-layout. Add-Gift
 * used to be the other button; it left with the #930 mechanism (#1905).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';
import { Button } from '@resource-kit/ui/react';
import { cn } from '@resource-kit/ui';
import { useToast } from '@components/ui/Toast';
import { useBetterAuth } from '@hooks/useAuth';
import { type Order } from '@/services';
import { REFETCH_RESULT_KINDS, type RefetchResult } from '@/services/orders';
import { useRefetchFromShopify } from '../hooks/useRefetchFromShopify';

type RefetchFeedback = {
  toast: 'success' | 'error';
  titleKey: string;
  messageKey: string;
  appendProjectionHint: boolean;
};

const REFETCH_FEEDBACK_BY_KIND = {
  [REFETCH_RESULT_KINDS.APPLIED]: {
    toast: 'success',
    titleKey: 'order.refetch.success.title',
    messageKey: 'order.refetch.success.projectionOnlyHint',
    appendProjectionHint: false,
  },
  [REFETCH_RESULT_KINDS.STALE_EVENT]: {
    toast: 'success',
    titleKey: 'order.refetch.success.title',
    messageKey: 'order.refetch.success.stale',
    appendProjectionHint: true,
  },
  [REFETCH_RESULT_KINDS.NOT_FOUND_IN_SHOPIFY]: {
    toast: 'error',
    titleKey: 'order.refetch.error.title',
    messageKey: 'order.refetch.error.notFound',
    appendProjectionHint: false,
  },
} as const satisfies Record<RefetchResult['kind'], RefetchFeedback>;

export function OrderActionsToolbar({ order }: { order: Order }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { isAdmin } = useBetterAuth();

  // Server-side gate is `requireAdmin()` in the order-refetch route. The
  // UI mirrors that gate so non-admin operators don't see a button that
  // always 403s.
  const canRefetch = isAdmin;

  const refetchMutation = useRefetchFromShopify(order.shopify_id);
  // 5-second cross-request cooldown after a refetch settles (success or
  // error). `isPending` disables the button during one in-flight request,
  // but a fast operator can re-click the instant it settles — each click
  // costs a Shopify Admin GraphQL bucket charge. `Date.now()` in render is
  // not reactive (#626), so track `cooldownActive` as state + a timer.
  const [cooldownUntil, setCooldownUntil] = useState<number>(0);
  const [cooldownActive, setCooldownActive] = useState<boolean>(false);
  useEffect(() => {
    if (cooldownUntil === 0) {
      setCooldownActive(false);
      return;
    }
    const remainingMs = cooldownUntil - Date.now();
    if (remainingMs <= 0) {
      setCooldownActive(false);
      return;
    }
    setCooldownActive(true);
    const timer = setTimeout(() => setCooldownActive(false), remainingMs);
    return () => clearTimeout(timer);
  }, [cooldownUntil]);

  const handleRefetch = async () => {
    if (refetchMutation.isPending || Date.now() < cooldownUntil) return;
    try {
      const result = await refetchMutation.mutateAsync();
      const feedback = REFETCH_FEEDBACK_BY_KIND[result.kind];
      const message = t(feedback.messageKey);
      const detail = feedback.appendProjectionHint
        ? `${message} ${t('order.refetch.success.projectionOnlyHint')}`
        : message;
      toast[feedback.toast](t(feedback.titleKey), detail);
    } catch (err) {
      const detail = err instanceof Error ? err.message : String(err);
      toast.error(t('order.refetch.error.title'), t('order.refetch.error.unknown', { detail }));
    } finally {
      setCooldownUntil(Date.now() + 5000);
    }
  };

  // Self-gating: no grant at all → render nothing (no empty toolbar).
  if (!canRefetch) return null;

  return (
    <div data-testid="order-actions-toolbar" className="flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        variant="outline"
        onClick={handleRefetch}
        disabled={refetchMutation.isPending || cooldownActive}
        aria-busy={refetchMutation.isPending}
        className="gap-2"
      >
        <RefreshCw className={cn('w-4 h-4', refetchMutation.isPending && 'animate-spin')} />
        {refetchMutation.isPending ? t('ui.processing') : t('order.refetch.button')}
      </Button>
    </div>
  );
}
